import React from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableBatchActions,
  TableBatchAction,
  TableSelectAll,
  Pagination
} from 'carbon-components-react';
import { ArrowUp16, ArrowsVertical16, ArrowDown16 } from '@carbon/icons-react';
import { toLocalDateTimeFormat } from 'utilities/helper.util';
import { useDispatch } from 'react-redux';
import { Header } from 'redux/member/types';
import DisplayRow from './row';
import { convertEventStatusTolabel } from 'appConstants/webhook';
import EmptyStatePage from './empty';
import { setNotificationHistoryOrderBy } from 'redux/webhook/actions';

const PageSizes = [10, 20, 50];
const httpStatusOK = [200, 201];

const ShowSortOrder: React.FC<any> = ({
  colName,
  sortOrder,
  setSortOrder
}): any => {
  const dispatch: any = useDispatch();
  const handleSortOrder = () => {
    setSortOrder({
      sortedBy: colName,
      sortedOrder: sortOrder.sortedOrder === 'A' ? 'D' : 'A'
    });
    colName = sortOrder.sortedOrder === 'A' ? colName : `-${colName}`;
    dispatch(setNotificationHistoryOrderBy(colName));
  };
  return (
    <div onClick={handleSortOrder}>
      {colName !== sortOrder.sortedBy ? (
        <ArrowsVertical16 />
      ) : sortOrder.sortedOrder === 'A' ? (
        <ArrowDown16 />
      ) : (
        <ArrowUp16 />
      )}
    </div>
  );
};

const WebhookHistoryTable: React.FC<any> = ({
  notificationHistory,
  handleViewDetail,
  handlePageChange,
  currentPage,
  pageSize,
  totalItems,
  handleRetryClick,
  setTotalSelectedRows,
  totalSelectedRows,
  sortOrder,
  setSortOrder
}) => {
  const headers: Array<Header> = [
    {
      key: 'eventType',
      header: 'Event type',
      isSortable: false
    },
    { key: 'eventTime', header: 'Event time', isSortable: false },
    { key: 'summary', header: 'Summary' },
    { key: 'eventStatus', header: 'Event status' },
    { key: 'errorDetails', header: 'Error details' },
    { key: 'subscriptionId', header: '' }
  ];

  const filteredNotifications = notificationHistory?.map((history: any) => ({
    id: history.id.toString(),
    eventType: history.subscriptionType,
    eventTime: toLocalDateTimeFormat(history.createdAt),
    summary: [
      history.id,
      history.jobNumber,
      history.sequenceNumber,
      history.email
    ],
    eventStatus: `${convertEventStatusTolabel(history.status)}`,
    errorDetails: httpStatusOK.includes(history.lastResponseCode)
      ? ''
      : `Status code: ${history.lastResponseCode}`,
    subscriptionId: history.subscriptionId
  }));

  return (
    <>
      <DataTable rows={filteredNotifications} headers={headers}>
        {({
          rows,
          getTableProps,
          getHeaderProps,
          getRowProps,
          getBatchActionProps,
          selectedRows,
          getSelectionProps
        }: any) => {
          return (
            <TableContainer>
              <TableToolbar>
                <TableToolbarContent></TableToolbarContent>
                <TableBatchActions
                  {...getBatchActionProps()}
                  shouldShowBatchActions={totalSelectedRows > 0 ? true : false}
                  totalSelected={totalSelectedRows}
                >
                  {setTotalSelectedRows(getBatchActionProps().totalSelected)}
                  <TableBatchAction
                    tabIndex={
                      getBatchActionProps().shouldShowBatchActions ? 0 : -1
                    }
                    onClick={() => handleRetryClick(selectedRows)}
                  >
                    Resubmit selected
                  </TableBatchAction>
                </TableBatchActions>
              </TableToolbar>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableSelectAll {...getSelectionProps()} />
                    <TableHeader
                      key="eventType"
                      {...getHeaderProps({
                        key: 'eventType',
                        header: 'Event type'
                      })}
                    >
                      <div className="webhook-history-sort-header">
                        <div>Event type</div>
                        <ShowSortOrder
                          colName="webhook"
                          sortOrder={sortOrder}
                          setSortOrder={setSortOrder}
                        />
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="createdAt"
                      {...getHeaderProps({
                        key: 'createdAt',
                        header: 'Event time'
                      })}
                    >
                      <div className="webhook-history-sort-header">
                        <div>Event initiated</div>
                        <ShowSortOrder
                          colName="createdAt"
                          sortOrder={sortOrder}
                          setSortOrder={setSortOrder}
                        />
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="summary"
                      {...getHeaderProps({ key: 'summary', header: 'Summary' })}
                    >
                      <div className="webhook-history-sort-header">
                        <div>Summary</div>
                        <ShowSortOrder
                          colName="id"
                          sortOrder={sortOrder}
                          setSortOrder={setSortOrder}
                        />
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="eventStatus"
                      {...getHeaderProps({
                        key: 'eventStatus',
                        header: 'Event status'
                      })}
                    >
                      <div className="webhook-history-sort-header">
                        <div>Event status</div>
                        <ShowSortOrder
                          colName="status"
                          sortOrder={sortOrder}
                          setSortOrder={setSortOrder}
                        />
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="errorDetails"
                      {...getHeaderProps({
                        key: 'errorDetails',
                        header: 'Error details'
                      })}
                    >
                      <div className="webhook-history-sort-header">
                        <div>Error details</div>
                        <ShowSortOrder
                          colName="lastResponseCode"
                          sortOrder={sortOrder}
                          setSortOrder={setSortOrder}
                        />
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="subscriptionId"
                      {...getHeaderProps({ key: 'subscriptionId', header: '' })}
                    ></TableHeader>
                    <TableHeader />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: any, index: any) => (
                    <DisplayRow
                      row={row}
                      getRowProps={getRowProps}
                      key={index}
                      getSelectionProps={getSelectionProps}
                      handleViewDetail={handleViewDetail}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>
      {notificationHistory?.length < 1 && <EmptyStatePage />}
      {notificationHistory?.length >= 1 && (
        <Pagination
          onChange={(data) => handlePageChange(data)}
          backwardText="Previous page"
          forwardText="Next page"
          itemsPerPageText="Items per page:"
          page={currentPage}
          pageNumberText="Page Number"
          pageSize={pageSize}
          pageSizes={PageSizes}
          totalItems={totalItems}
        />
      )}
    </>
  );
};
WebhookHistoryTable.displayName = 'WebhookHistoryTable';
export default WebhookHistoryTable;
